import React, { memo } from 'react';
import { X } from 'lucide-react';
import { CONTEXT_ALERT_LEVELS, CONTEXT_WARNING_THRESHOLD, AGENT_STATUS } from '../constants';

/**
 * Context usage warning component
 * Shows when thread context usage is high and agent is stopped
 */
const ContextWarning = memo(({ 
  contextUsage, 
  agentStatus, 
  dismissedContextUsage,
  onDismiss,
  onNewThread 
}) => {
  // Don't show if context is dismissed, usage is low, or agent is generating
  if (!contextUsage || 
      agentStatus !== AGENT_STATUS.STOPPED || 
      contextUsage.usage_percentage < CONTEXT_WARNING_THRESHOLD ||
      JSON.stringify(contextUsage) === JSON.stringify(dismissedContextUsage)) {
    return null;
  }

  const getAlertStyles = (alertLevel) => {
    switch (alertLevel) {
      case CONTEXT_ALERT_LEVELS.CRITICAL:
        return {
          container: 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900',
          icon: 'text-red-600 dark:text-red-400',
          title: 'text-red-800 dark:text-red-200',
          message: 'text-red-700 dark:text-red-300',
          button: 'bg-red-600 hover:bg-red-700 text-white',
          dismiss: 'text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 hover:bg-red-100 dark:hover:bg-red-900/20'
        };
      case CONTEXT_ALERT_LEVELS.WARNING:
        return {
          container: 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900',
          icon: 'text-amber-600 dark:text-amber-400',
          title: 'text-amber-800 dark:text-amber-200',
          message: 'text-amber-700 dark:text-amber-300',
          button: 'bg-amber-600 hover:bg-amber-700 text-white',
          dismiss: 'text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/20'
        };
      case CONTEXT_ALERT_LEVELS.INFO:
        return {
          container: 'bg-amber-50/70 dark:bg-amber-950/20 border-amber-200/70 dark:border-amber-900/70',
          icon: 'text-amber-600/80 dark:text-amber-400/80',
          title: 'text-amber-800/90 dark:text-amber-200/90',
          message: 'text-amber-700/90 dark:text-amber-300/90',
          button: 'bg-amber-500 hover:bg-amber-600 text-white',
          dismiss: 'text-amber-600/80 dark:text-amber-400/80 hover:text-amber-800 dark:hover:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/20'
        };
      default:
        return {
          container: 'bg-neutral-50 dark:bg-neutral-950/20 border-neutral-200 dark:border-neutral-800',
          icon: 'text-neutral-600 dark:text-neutral-400',
          title: 'text-neutral-800 dark:text-neutral-200',
          message: 'text-neutral-700 dark:text-neutral-300',
          button: 'bg-neutral-600 hover:bg-neutral-700 text-white',
          dismiss: 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-900/20'
        };
    }
  };

  const styles = getAlertStyles(contextUsage.alert_level);

  return (
    <div className={`mb-0 p-3 rounded-xl border ${styles.container}`}>
      <div className="flex items-start gap-2">
        <svg className={`w-5 h-5 flex-shrink-0 mt-0.5 ${styles.icon}`} fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
        <div className="flex-1">
          <p className={`text-sm font-medium mb-1 ${styles.title}`}>
            Thread Context: {contextUsage.usage_percentage}% Used
          </p>
          <p className={`text-xs ${styles.message}`}>
            {contextUsage.message || `${contextUsage.input_tokens.toLocaleString()} of ${contextUsage.max_tokens.toLocaleString()} tokens used. Compression at ${contextUsage.compression_threshold}%.`}
          </p>
        </div>
        <button
          onClick={onDismiss}
          className={`p-1 rounded-lg transition-colors ${styles.dismiss}`}
          title="Dismiss"
          aria-label="Dismiss context usage warning"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
});

ContextWarning.displayName = 'ContextWarning';

export default ContextWarning;